import { useCallback, useRef } from 'react';
import { message } from 'antd';
import { customInstance } from '@/shared/api/mutator/custom-instance';
import { useBatchTransactionStore } from '../stores/batch-transaction-store';
import type { BatchJobDTO, BatchPreviewDTO, BatchResultDTO } from '../types';

// ── API ───────────────────────────────────────────────────────────────────

const apiCreateJob = (formData: FormData): Promise<BatchJobDTO> =>
  customInstance({
    url: '/api/batch-transactions/jobs',
    method: 'POST',
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' },
  });

const apiListJobs = (): Promise<BatchJobDTO[]> =>
  customInstance({ url: '/api/batch-transactions/jobs', method: 'GET' });

const apiGetJob = (id: number): Promise<BatchJobDTO> =>
  customInstance({ url: `/api/batch-transactions/jobs/${id}`, method: 'GET' });

const apiGetPreview = (id: number): Promise<BatchPreviewDTO> =>
  customInstance({ url: `/api/batch-transactions/jobs/${id}/preview`, method: 'GET' });

const apiSubmitJob = (id: number): Promise<BatchResultDTO> =>
  customInstance({ url: `/api/batch-transactions/jobs/${id}/submit`, method: 'POST' });

const apiGetResult = (id: number): Promise<BatchResultDTO> =>
  customInstance({ url: `/api/batch-transactions/jobs/${id}/results`, method: 'GET' });

// ── Hook ──────────────────────────────────────────────────────────────────

const TERMINAL_STATUSES = new Set(['COMPLETED', 'PARTIALLY_COMPLETED', 'FAILED']);

export const useBatchJob = () => {
  const {
    jobs,
    jobsLoading,
    activeJobId,
    previewData,
    submitResult,
    isSubmitting,
    setJobs,
    setJobsLoading,
    setActiveJobId,
    setPreviewData,
    setSubmitResult,
    setIsSubmitting,
    setUploadedFileName,
    setWizardStep,
  } = useBatchTransactionStore();

  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current !== null) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const loadJobs = useCallback(async () => {
    setJobsLoading(true);
    try {
      const data = await apiListJobs();
      setJobs(data);
    } catch {
      message.error('Failed to load batch jobs');
    } finally {
      setJobsLoading(false);
    }
  }, [setJobs, setJobsLoading]);

  const createJob = useCallback(
    async (
      file: File,
      configId: number,
      transactionDate?: string,
      narration?: string,
    ): Promise<BatchJobDTO | null> => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('configId', String(configId));
      if (transactionDate) formData.append('transactionDate', transactionDate);
      if (narration) formData.append('narration', narration);

      try {
        const job = await apiCreateJob(formData);
        setActiveJobId(job.id);
        setUploadedFileName(file.name);
        return job;
      } catch (err: any) {
        message.error(err?.response?.data?.message || 'Failed to upload batch file');
        return null;
      }
    },
    [setActiveJobId, setUploadedFileName],
  );

  const fetchPreview = useCallback(
    async (jobId: number): Promise<BatchPreviewDTO | null> => {
      try {
        const preview = await apiGetPreview(jobId);
        setPreviewData(preview);
        return preview;
      } catch (err: any) {
        message.error(err?.response?.data?.message || 'Failed to load preview');
        return null;
      }
    },
    [setPreviewData],
  );

  const submitJob = useCallback(
    async (jobId: number): Promise<BatchResultDTO | null> => {
      setIsSubmitting(true);
      try {
        const result = await apiSubmitJob(jobId);
        setSubmitResult(result);
        return result;
      } catch (err: any) {
        message.error(err?.response?.data?.message || 'Submission failed');
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [setIsSubmitting, setSubmitResult],
  );

  const pollJobUntilTerminal = useCallback(
    (jobId: number, onComplete: (job: BatchJobDTO) => void, intervalMs = 2000) => {
      stopPolling();

      const poll = async () => {
        try {
          const job = await apiGetJob(jobId);
          if (TERMINAL_STATUSES.has(job.status)) {
            onComplete(job);
            return;
          }
        } catch {
          // silently retry
        }
        pollTimerRef.current = setTimeout(poll, intervalMs);
      };

      pollTimerRef.current = setTimeout(poll, intervalMs);
    },
    [stopPolling],
  );

  const fetchResult = useCallback(
    async (jobId: number): Promise<BatchResultDTO | null> => {
      try {
        const result = await apiGetResult(jobId);
        setSubmitResult(result);
        return result;
      } catch (err: any) {
        message.error(err?.response?.data?.message || 'Failed to load results');
        return null;
      }
    },
    [setSubmitResult],
  );

  const downloadResultsCsv = useCallback((result: BatchResultDTO) => {
    const header = 'Row,Member ID,Account No,Status,Posted Amount,Error\n';
    const rows = result.items
      .map((item) =>
        [
          item.rowNumber,
          item.memberId ?? '',
          item.accountNo ?? '',
          item.status,
          item.postedAmount ?? '',
          (item.errorMessage ?? '').replace(/,/g, ';'),
        ].join(','),
      )
      .join('\n');

    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `batch-results-job-${result.jobId}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  }, []);

  const resetAndStartNew = useCallback(() => {
    stopPolling();
    useBatchTransactionStore.getState().resetWizard();
    setWizardStep(0);
  }, [stopPolling, setWizardStep]);

  return {
    jobs,
    jobsLoading,
    activeJobId,
    previewData,
    submitResult,
    isSubmitting,
    loadJobs,
    createJob,
    fetchPreview,
    submitJob,
    pollJobUntilTerminal,
    fetchResult,
    downloadResultsCsv,
    stopPolling,
    resetAndStartNew,
  };
};
